// Design System Colors - Based on Figma Palette
// Organized following Tailwind CSS recommended approach

export const COLORS = {
  // Primary Blue - Main brand color
  'blue-25': '#FAFDFF',
  'blue-50': '#F5FAFF',
  'blue-100': '#E2F0FD',
  'blue-200': '#BBDDFB',
  'blue-300': '#8BC4F8',
  'blue-400': '#49A2F4',
  'blue-500': '#1C8BF1',
  'blue-600': '#197EDB',
  'blue-700': '#0058A8', // Primary
  'blue-800': '#004280',
  'blue-900': '#003566',
  'blue-950': '#00203E',

  // Dark Blue - Secondary brand color
  'dark-blue-25': '#F2F3F7',
  'dark-blue-50': '#E8EAF1',
  'dark-blue-100': '#B9BFD3',
  'dark-blue-200': '#979FBE',
  'dark-blue-300': '#6774A1',
  'dark-blue-400': '#49598E',
  'dark-blue-500': '#1C2F72',
  'dark-blue-600': '#192B68',
  'dark-blue-700': '#142151',
  'dark-blue-800': '#0F1A3F',
  'dark-blue-900': '#0C1430',

  // Black - Neutral colors
  'black-25': '#FCFCFD',
  'black-50': '#F9FAFB',
  'black-100': '#F2F4F7',
  'black-200': '#EAECF0',
  'black-300': '#D0D5DD',
  'black-400': '#98A2B3',
  'black-500': '#667085',
  'black-600': '#475467',
  'black-700': '#344054',
  'black-800': '#1D2939',
  'black-900': '#101828',
  'black-950': '#0C111D',

  // Grey - Neutral colors with semantic names
  'grey-25': '#FAFAFA',
  'grey-50': '#F2F2F2', // Stroke Neutral - Very Light
  'grey-100': '#EBEBEB', // Stroke Neutral - Light
  'grey-200': '#E0E0E0',
  'grey-300': '#C0C0C0', // Stroke Neutral - Medium
  'grey-400': '#A0A0A0',
  'grey-500': '#808080',
  'grey-600': '#696969', // Disabled Text
  'grey-700': '#515151', // Tertiary Text
  'grey-800': '#404040', // Secondary Text
  'grey-900': '#212121', // Primary Text
  'grey-950': '#121212',

  // Taupe - Background and UI colors
  'taupe-50': '#FBFAF9', // Background - Light
  'taupe-100': '#FAF9F8',
  'taupe-200': '#F9F8F6',
  'taupe-300': '#F8F4F3',
  'taupe-400': '#F4F1EF', // Background - Medium
  'taupe-500': '#EAE4E1', // Sidebar-Selected
  'taupe-550': '#E5E2E1', // Stroke - Light
  'taupe-600': '#E1DDDB', // Stroke - Medium
  'taupe-700': '#8E7E7B',
  'taupe-800': '#695A56',
  'taupe-900': '#504B49', // Sidebar-Text
  'taupe-950': '#282625',

  // Green - Success and positive states
  'green-25': '#F7FDFB',
  'green-50': '#ECFAF5',
  'green-100': '#C4E7DA',
  'green-200': '#9CD4C0',
  'green-300': '#74C0A4',
  'green-400': '#4EAC8B',
  'green-500': '#09865A',
  'green-600': '#1E7A5A',
  'green-700': '#155C43',
  'green-800': '#0F3D2D',
  'green-900': '#071F17',
  'green-950': '#040F0B',

  // Yellow - Warning and attention states
  'yellow-50': '#FFFBF5',
  'yellow-100': '#FEEDD6',
  'yellow-200': '#FFDDAD',
  'yellow-300': '#FDCD84',
  'yellow-400': '#FDBC5C',
  'yellow-500': '#FCAB32',
  'yellow-600': '#E58B03',
  'yellow-700': '#BB7103',
  'yellow-800': '#915802',
  'yellow-900': '#673E01',
  'yellow-950': '#3C2401',

  // Red - Error and destructive states
  'red-25': '#FEF6F6',
  'red-50': '#FDEDED',
  'red-100': '#FCDFDF',
  'red-200': '#F49F9F',
  'red-300': '#F07979',
  'red-400': '#EC5252',
  'red-500': '#E82E2E',
  'red-600': '#D21717',
  'red-700': '#AB1313',
  'red-800': '#840F0F',
  'red-900': '#5E0A0A',

  // Orange - Alert and notification states
  'orange-50': '#FEF0E9',
  'orange-100': '#FBD1BA',
  'orange-200': '#F9BB99',
  'orange-300': '#F69C6B',
  'orange-400': '#F5894E',
  'orange-500': '#F26B22',
  'orange-600': '#DC611F',
  'orange-700': '#AC4C18',
  'orange-800': '#853B13',
  'orange-900': '#662D0E',

  // Secondary Colors - Accent colors
  lime: '#A5DD8C',
  peach: '#F49B82',
  'light-blue': '#8BC4F8',
  'light-yellow': '#F8DC8B',
  'light-violet': '#AFB1FF',
  pink: '#F8AFF0',

  // Base Colors
  black: '#000000',
  white: '#FFFFFF',

  // Legacy colors (keeping for backward compatibility)
  'primary-25': '#F5FAFF',
  'primary-50': '#EFF8FF',
  'primary-100': '#D1E9FF',
  'primary-200': '#B2DDFF',
  'primary-300': '#84CAFF',
  'primary-400': '#53B1FD',
  'primary-500': '#2E90FA',
  'primary-600': '#1570EF',
  'primary-700': '#175CD3',
  'primary-800': '#1849A9',
  'primary-900': '#194185',
  'primary-950': '#102A56',
  'primary-dark': '#163076',
  'primary-dark-100': '#B9BFD3',
  'primary-dark-500': '#1C2F72',
  'primary-dark-600': '#192B68',
  'primary-dark-700': '#142151',
  'secondary-blue-100': '#E2F0FD',
  'secondary-blue-700': '#1463AB',
  'redd-25': '#FFFBFA',
  'redd-100': '#FEE4E2',
  'redd-300': '#FDA29B',
  'redd-600': '#D92D20',
  'redd-700': '#B53824',
  'redd-new-25': '#FFF6F5',
  'redd-new-600': '#E8482E',
  'redd-new-300': '#FF8976',
  'greene-25': '#F6FEF9',
  'greene-200': '#BADED6',
  'greene-300': '#75E0A7',
  'greene-500': '#25997F',
  'greene-600': '#079455',
  'orangee-500': '#F04438',
  'yelloww-500': '#F9B855',
} as const;

// Semantic color mappings for consistent usage
export const SEMANTIC_COLORS = {
  // Brand colors
  primary: COLORS['blue-700'],
  secondary: COLORS['dark-blue-500'],

  // Text colors
  'text-primary': COLORS['grey-900'],
  'text-secondary': COLORS['grey-800'],
  'text-tertiary': COLORS['grey-700'],
  'text-disabled': COLORS['grey-600'],

  // Background colors
  'bg-primary': COLORS.white,
  'bg-secondary': COLORS['taupe-50'],
  'bg-tertiary': COLORS['taupe-400'],
  'bg-sidebar': COLORS['taupe-500'],

  // Border colors
  'border-light': COLORS['grey-50'],
  'border-medium': COLORS['grey-300'],
  'border-dark': COLORS['grey-600'],
  'border-taupe-light': COLORS['taupe-550'],
  'border-taupe-medium': COLORS['taupe-600'],

  // Status colors
  success: COLORS['green-500'],
  warning: COLORS['yellow-500'],
  error: COLORS['red-500'],
  info: COLORS['blue-500'],
} as const;

// Color scale utilities for consistent shade generation
export const COLOR_SCALES = {
  blue: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
  darkBlue: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
  ],
  grey: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
  taupe: [
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '550',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
  green: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
  yellow: [
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
  red: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
  ],
  orange: ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'],
  black: [
    '25',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '950',
  ],
} as const;

// Type definitions for better TypeScript support
export type ColorName = keyof typeof COLORS;
export type SemanticColorName = keyof typeof SEMANTIC_COLORS;
export type ColorScaleName = keyof typeof COLOR_SCALES;
